import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { translateLyrics } from '../lib/openai';
import { FaSun, FaMoon, FaFont, FaVolumeUp, FaVolumeMute, FaBookmark, FaPlay, FaPause, FaStepBackward, FaStepForward, FaArrowLeft, FaLanguage, FaSpinner, FaEllipsisV } from 'react-icons/fa';
import toast from 'react-hot-toast';

interface Story {
  id: string;
  title: string;
  content: string;
  languages: string[];
  audio_url: string;
  category: string;
  live_translate: boolean;
  is_promoted: boolean;
  likes: number;
  is_favorite: boolean;
  is_paid: boolean;
  cover_image: string | null;
}

interface TranslationMap {
  [paragraphIndex: number]: string;
}

export default function Story() {
  const [story, setStory] = useState<Story | null>(null);
  const [loading, setLoading] = useState(true);
  const [darkMode, setDarkMode] = useState(false);
  const [fontSize, setFontSize] = useState(16);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [translations, setTranslations] = useState<TranslationMap>({});
  const [selectedLanguage, setSelectedLanguage] = useState<'english' | 'french'>('english');
  const [translating, setTranslating] = useState(false);
  const [showLanguageSelector, setShowLanguageSelector] = useState(false);
  const [wordByWordTranslation, setWordByWordTranslation] = useState<{[key: string]: string}>({});
  const [translatingParagraph, setTranslatingParagraph] = useState<number | null>(null);
  const [showMenu, setShowMenu] = useState(false);

  const audioRef = useRef<HTMLAudioElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    loadStory();
  }, [id]);

  useEffect(() => {
    document.body.classList.toggle('dark', darkMode);
    return () => document.body.classList.remove('dark');
  }, [darkMode]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : volume;
    }
  }, [volume, isMuted]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadStory = async () => {
    if (!id) return;
    try {
      const { data, error } = await supabase
        .from('stripeenstory')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      if (!data) {
        toast.error('Story not found');
        navigate('/stories');
        return;
      }

      setStory(data);
    } catch (error: any) {
      toast.error('Error loading story');
      navigate('/stories');
    } finally {
      setLoading(false);
    }
  };

  const handlePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleSeek = (value: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = value;
      setCurrentTime(value);
    }
  };

  const handleSkip = (seconds: number) => {
    if (audioRef.current) {
      const newTime = audioRef.current.currentTime + seconds;
      audioRef.current.currentTime = Math.max(0, Math.min(newTime, duration));
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getDefaultCoverImage = (category: string) => {
    switch (category?.toLowerCase()) {
      case 'adventure':
        return 'https://images.unsplash.com/photo-1519681393784-d120267933ba?w=800';
      case 'travel':
        return 'https://images.unsplash.com/photo-1501785888041-af3ef285b470?w=800';
      case 'mystery':
        return 'https://images.unsplash.com/photo-1478760329108-5c3ed9d495a0?w=800';
      default:
        return 'https://images.unsplash.com/photo-1512820790803-83ca734da794?w=800';
    }
  };

  const translateParagraph = async (paragraphIndex: number, text: string) => {
    if (translating) return;
    
    setTranslatingParagraph(paragraphIndex);
    setTranslating(true);
    
    try {
      const translation = await translateLyrics(text, selectedLanguage);
      if (translation) {
        setTranslations(prev => ({
          ...prev,
          [paragraphIndex]: translation
        }));
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to translate text');
    } finally {
      setTranslating(false);
      setTranslatingParagraph(null);
    }
  };

  const translateWordByWord = async (text: string) => {
    if (translating) return;
    
    setTranslating(true);
    setShowMenu(false);
    
    try {
      // Split the text into words
      const words = text.split(/\s+/);
      
      // Create a prompt for word-by-word translation
      const prompt = `Translate each of these words from the story from their original language to ${selectedLanguage}. Return a JSON object with each original word as the key and its translation as the value: ${words.join(', ')}`;
      
      const response = await translateLyrics(prompt, selectedLanguage);
      
      try {
        // Try to parse the response as JSON
        const jsonStart = response.indexOf('{');
        const jsonEnd = response.lastIndexOf('}') + 1;
        
        if (jsonStart >= 0 && jsonEnd > jsonStart) {
          const jsonStr = response.substring(jsonStart, jsonEnd);
          const translationObj = JSON.parse(jsonStr);
          setWordByWordTranslation(translationObj);
        } else {
          // If not valid JSON, create a simple mapping
          const lines = response.split('\n');
          const translationObj: {[key: string]: string} = {};
          
          lines.forEach(line => {
            const parts = line.split(':');
            if (parts.length >= 2) {
              const key = parts[0].trim().replace(/"/g, '');
              const value = parts[1].trim().replace(/"/g, '').replace(/,$/g, '');
              if (key && value) {
                translationObj[key] = value;
              }
            }
          });
          
          setWordByWordTranslation(translationObj);
        }
      } catch (parseError) {
        console.error('Error parsing translation:', parseError);
        toast.error('Failed to parse translation');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to translate text');
    } finally {
      setTranslating(false);
    }
  };

  const renderWordWithTranslation = (word: string) => {
    const cleanWord = word.replace(/[.,!?;:'"()]/g, '');
    const punctuation = word.substring(cleanWord.length);
    
    const translation = wordByWordTranslation[cleanWord] || wordByWordTranslation[cleanWord.toLowerCase()];
    
    return (
      <span className="relative group inline-block">
        {word}
        {translation && (
          <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 bg-black/80 text-red-500 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
            {translation}
          </span>
        )}
        {punctuation}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!story) {
    return null;
  }

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      darkMode ? 'bg-black text-yellow-300' : 'bg-white text-gray-900'
    }`}>
      {/* Controls */}
      <div className={`fixed top-16 left-0 right-0 z-10 ${
        darkMode ? 'bg-gray-900' : 'bg-gray-100'
      } shadow-md`}>
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/stories')}
              className={`p-2 rounded-lg ${
                darkMode ? 'bg-gray-800 text-yellow-300' : 'bg-gray-200 text-gray-700'
              }`}
            >
              <FaArrowLeft />
            </button>
            
            <button
              onClick={() => setDarkMode(!darkMode)}
              className={`p-2 rounded-lg ${
                darkMode ? 'bg-yellow-500 text-black' : 'bg-gray-200 text-gray-700'
              }`}
            >
              {darkMode ? <FaMoon /> : <FaSun />}
            </button>

            <div className="flex items-center space-x-2">
              <button
                onClick={() => setFontSize(Math.max(12, fontSize - 2))}
                className={`p-2 rounded-lg ${
                  darkMode ? 'bg-gray-800 text-yellow-300' : 'bg-gray-200 text-gray-700'
                }`}
              >
                <FaFont className="w-3 h-3" />
              </button>
              <span className={darkMode ? 'text-yellow-300' : 'text-gray-700'}>
                {fontSize}px
              </span>
              <button
                onClick={() => setFontSize(Math.min(24, fontSize + 2))}
                className={`p-2 rounded-lg ${
                  darkMode ? 'bg-gray-800 text-yellow-300' : 'bg-gray-200 text-gray-700'
                }`}
              >
                <FaFont className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Translation Menu */}
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setShowMenu(!showMenu)}
              className={`p-2 rounded-lg ${
                darkMode ? 'bg-gray-800 text-yellow-300' : 'bg-gray-200 text-gray-700'
              }`}
              aria-label="Translation options"
            >
              <FaEllipsisV />
            </button>
            
            {showMenu && (
              <div className="absolute right-0 mt-2 w-56 bg-black rounded-lg shadow-lg p-2 z-20">
                <div className="px-4 py-2 text-sm text-gray-400 border-b border-gray-800">
                  Translation Options
                </div>
                
                <div className="py-2">
                  <div className="px-4 py-2 text-sm text-gray-400">
                    Language
                  </div>
                  <button
                    onClick={() => {
                      setSelectedLanguage('english');
                      setTranslations({});
                      setWordByWordTranslation({});
                    }}
                    className={`block w-full text-left px-4 py-2 text-sm rounded-lg ${
                      selectedLanguage === 'english' ? 'bg-yellow-500 text-black' : 'text-white hover:bg-gray-800'
                    }`}
                  >
                    English
                  </button>
                  <button
                    onClick={() => {
                      setSelectedLanguage('french');
                      setTranslations({});
                      setWordByWordTranslation({});
                    }}
                    className={`block w-full text-left px-4 py-2 text-sm rounded-lg ${
                      selectedLanguage === 'french' ? 'bg-yellow-500 text-black' : 'text-white hover:bg-gray-800'
                    }`}
                  >
                    French
                  </button>
                </div>
                
                <div className="border-t border-gray-800 py-2">
                  <button
                    onClick={() => translateWordByWord(story.content)}
                    className={`flex items-center w-full text-left px-4 py-2 text-sm text-white hover:bg-gray-800 rounded-lg ${
                      translating ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                    disabled={translating}
                  >
                    {translating ? (
                      <>
                        <FaSpinner className="w-4 h-4 mr-2 animate-spin" />
                        Translating...
                      </>
                    ) : (
                      <>
                        <FaLanguage className="w-4 h-4 mr-2" />
                        Translate All
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Story Cover Image */}
      <div className="pt-24 pb-6 max-w-4xl mx-auto px-4">
        <div className="w-full h-64 md:h-96 rounded-xl overflow-hidden mb-8">
          <img 
            src={story?.cover_image || getDefaultCoverImage(story?.category || '')}
            alt={story?.title}
            className="w-full h-full object-cover"
          />
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4">
        {/* Minimalist Audio Player */}
        <div className="fixed bottom-0 left-0 right-0 bg-black bg-opacity-90 backdrop-blur-lg">
          <div className="max-w-4xl mx-auto px-4 py-2">
            <audio
              ref={audioRef}
              src={story?.audio_url}
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={handleLoadedMetadata}
              onEnded={() => setIsPlaying(false)}
            />
            
            {/* Progress Bar */}
            <div className="relative w-full h-1 bg-gray-700 rounded-full mb-2">
              <div 
                className="absolute left-0 top-0 h-full bg-yellow-400 rounded-full"
                style={{ width: `${(currentTime / duration) * 100}%` }}
              />
              <input
                type="range"
                min={0}
                max={duration || 0}
                value={currentTime}
                onChange={(e) => handleSeek(parseFloat(e.target.value))}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                aria-label="Progress"
              />
            </div>

            {/* Controls */}
            <div className="flex items-center justify-center space-x-8">
              <button
                onClick={() => handleSkip(-10)}
                className="text-gray-400 hover:text-white"
              >
                <FaStepBackward className="w-4 h-4" />
              </button>

              <button
                onClick={handlePlayPause}
                className="text-white hover:text-yellow-400"
              >
                {isPlaying ? 
                  <FaPause className="w-6 h-6" /> : 
                  <FaPlay className="w-6 h-6" />
                }
              </button>

              <button
                onClick={() => handleSkip(10)}
                className="text-gray-400 hover:text-white"
              >
                <FaStepForward className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="pb-16">
          <h1 className={`text-3xl font-bold mb-8 ${
            darkMode ? 'text-yellow-300' : 'text-gray-900'
          }`}>
            {story?.title}
          </h1>

          <div ref={contentRef} className="space-y-6" style={{ fontSize: `${fontSize}px` }}>
            {story?.content.split('\n\n').map((paragraph, index) => (
              <div key={index} className="space-y-2">
                <p className={`leading-relaxed ${
                  darkMode ? 'text-yellow-300' : 'text-gray-900'
                }`}>
                  {Object.keys(wordByWordTranslation).length > 0 ? (
                    paragraph.split(/\s+/).map((word, wordIndex) => (
                      <span key={wordIndex} className="mr-1">
                        {renderWordWithTranslation(word)}
                      </span>
                    ))
                  ) : (
                    paragraph
                  )}
                </p>
                
                {!Object.keys(wordByWordTranslation).length > 0 && (
                  translations[index] ? (
                    <p className="text-red-500 italic text-base">
                      {translations[index]}
                    </p>
                  ) : (
                    <button
                      onClick={() => translateParagraph(index, paragraph)}
                      className={`text-sm ${darkMode ? 'text-indigo-400' : 'text-indigo-600'} hover:underline ${
                        translatingParagraph === index ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                      disabled={translatingParagraph === index}
                    >
                      {translatingParagraph === index ? (
                        <span className="flex items-center">
                          <FaSpinner className="animate-spin mr-1" />
                          Translating...
                        </span>
                      ) : (
                        `Translate to ${selectedLanguage}`
                      )}
                    </button>
                  )
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}